import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col, Badge, Button, Input, Spinner, Alert,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { ArrowLeft, Check, Eye, Plus, Trash2 } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import FormFillView from "../../../Components/FormFill/FormFillView";
import NcTreePanel from "../../../Components/FormFill/NcTreePanel";
import {
  createItem, updateItem, softDeleteItem, listItems,
} from "../../../services/inspectionAdminService";
import { getDepartments, getScopes, getTypes, getFormTree } from "../../../services/sahaInspectionService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// ─────────────────────────────────────────────────────────────────────────────
// Form düzenleyici — şablonu "Denetim Formu Doldur" sayfasındaki çizimiyle gösterir ve
// yerinde düzenletir: form adı / kısa kod / ana soru, kategori ve soru metinleri, cevap
// tipi, fotoğraf bayrakları, ekle / sil. Her değişiklik, Hiyerarşi
// Yönetimi'nin kullandığı uçlara (/inspection/types|categories|questions) hemen yazılır;
// bir Kaydet düğmesi yok. "Yeni Form": ad + kapsam ile tip oluşturulur, düzenleyici açılır.
// ─────────────────────────────────────────────────────────────────────────────

const ANSWER_TYPE_KEYS = { 1: "uygunUygunDegil", 2: "evetHayir", 3: "varYok", 4: "problemVarYok" };
// Sıra: doldurma sayfasının okuduğu GET /inspection/generate sırası (id sırası; sort_order'ı yok sayar, liste
// uçları ise sort_order'a göre sıralar) — taşıma düğmesi bu yüzden yok: reorder doldurma sayfasını değiştirmezdi.
// Onda olmayan öğe (ör. boş kategori) sona, kendi sort_order'ına göre.
const bySort = (a, b) => (a.sort_order ?? 1e9) - (b.sort_order ?? 1e9);
const rankOf = (ids) => { const m = new Map(); ids.forEach((x, i) => m.set(Number(x), i)); return (x) => m.get(Number(x)) ?? 1e9; };

// Alan kaybında / Enter'da kaydeden metin kutusu. Değer dışarıdan değişince `key` ile yeniden kurulur.
function InlineText({ value, onSave, required = true, upper = false, className = "", ...rest }) {
  const [draft, setDraft] = useState(value ?? "");
  function commit() {
    const v = upper ? draft.trim().toUpperCase() : draft.trim();
    if (required && !v) { setDraft(value ?? ""); return; }
    if (v === (value ?? "")) { setDraft(v); return; }
    onSave(v);
  }
  return (
    <Input
      bsSize="sm"
      className={className}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setDraft(value ?? ""); e.currentTarget.blur(); }
      }}
      {...rest}
    />
  );
}

// Kategori / soru ekleme satırı
function AddRow({ placeholder, withType, onAdd, onCancel }) {
  const { t: tx } = useTranslation("formlar");
  const [text, setText] = useState("");
  const [questType, setQuestType] = useState(1);
  const submit = () => { if (text.trim()) onAdd(text.trim(), questType); };
  return (
    <div className="d-flex gap-2 flex-wrap align-items-center p-3 border-top bg-light">
      <Input
        autoFocus
        bsSize="sm"
        className="flex-grow-1"
        style={{ minWidth: 220, width: "auto" }}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") onCancel();
        }}
      />
      {withType && (
        <Input type="select" bsSize="sm" style={{ width: "auto" }} value={questType}
          onChange={(e) => setQuestType(Number(e.target.value))}>
          {Object.keys(ANSWER_TYPE_KEYS).map((v) => (
            <option key={v} value={v}>{tx(`inspectionHierarchy.${ANSWER_TYPE_KEYS[v]}`)}</option>
          ))}
        </Input>
      )}
      <Button size="sm" color="primary" disabled={!text.trim()} onClick={submit}>{tx("common:ekle")}</Button>
      <Button size="sm" color="light" onClick={onCancel}>{tx("common:iptal")}</Button>
    </div>
  );
}

function FormEditor({ id }) {
  const { t: tx } = useTranslation("formlar");
  const navigate = useNavigate();
  const isNew = !id;

  const [scopes,       setScopes]       = useState([]);   // [{ id, label }]
  const [type,         setType]         = useState(null);  // { name, shortName, mainQuestion, mainQuestType, scopeId }
  const [cats,         setCats]         = useState([]);    // FormFillView ile aynı şekil
  const [loading,      setLoading]      = useState(!isNew);
  const [loadError,    setLoadError]    = useState(false);
  const [saveState,    setSaveState]    = useState("idle"); // idle | saving | saved | error
  const [errorMsg,     setErrorMsg]     = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);   // { kind: "cat" | "q", id, name }
  const [ncQuestion,   setNcQuestion]   = useState(null);   // NcTreePanel için seçili soru
  const [adding,       setAdding]       = useState(null);   // { kind: "cat" } | { kind: "q", catId }

  // Yeni form taslağı
  const [draft,    setDraft]    = useState({ name: "", shortName: "", scopeId: "", mainQuestion: "", mainQuestType: 1 });
  const [draftErr, setDraftErr] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [deps, scs] = await Promise.all([getDepartments(), getScopes()]);
      const deptName = {};
      (deps || []).forEach((d) => { deptName[d.inspection_department_id] = d.translated_name || d.inspection_department_name; });
      setScopes((scs || []).map((s) => {
        const sc = s.translated_name || s.inspection_scope_name;
        const dn = s.inspection_department_id != null ? deptName[s.inspection_department_id] : null;
        return { id: s.inspection_scope_id, label: dn ? `${dn} › ${sc}` : sc };
      }));
      if (!id) return;

      const catItems = await listItems("categories", { parentId: id });
      const [qLists, typeLists, tree] = await Promise.all([
        Promise.all(catItems.map((c) => listItems("questions", { parentId: c.inspection_category_id }))),
        Promise.all((scs || []).map((s) => getTypes(s.inspection_scope_id))),
        getFormTree(id),
      ]);
      const catRank = rankOf((tree?.categories || []).map((c) => c.categoryId));
      const qRank   = rankOf((tree?.categories || []).flatMap((c) => c.questions.map((q) => q.questionId)));
      const ty = typeLists.flat().find((x) => Number(x.inspection_type_id) === Number(id));
      if (!ty) throw new Error("type not found");

      setType({
        name:          ty.translated_name || ty.inspection_type_name || "",
        shortName:     ty.translated_short || ty.inspection_type_short || "",
        mainQuestion:  ty.main_question || "",
        mainQuestType: Number(ty.main_question_quest_type) || 1,
        scopeId:       ty.inspection_scope_id,
      });
      setCats(
        catItems
          .map((c, i) => ({ c, qs: qLists[i] || [] }))
          .sort((a, b) => (catRank(a.c.inspection_category_id) - catRank(b.c.inspection_category_id)) || bySort(a.c, b.c))
          .map(({ c, qs }) => ({
            categoryId:   c.inspection_category_id,
            categoryName: c.translated_name || c.inspection_category_name || "",
            questions: [...qs]
              .sort((a, b) => (qRank(a.inspection_question_id) - qRank(b.inspection_question_id)) || bySort(a, b))
              .map((q) => ({
                questionId:            q.inspection_question_id,
                questionName:          q.translated_name || q.inspection_question_name || "",
                questType:             Number(q.quest_type) || 1,
                photoRequiredPositive: !!Number(q.isPhotoRequiredPositive),
                photoRequiredNeutral:  !!Number(q.isPhotoRequiredNeutral),
              })),
          }))
      );
      setLoadError(false);
    } catch (err) {
      console.error("[FormEditorPage] load:", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    document.title = i18n.t(isNew ? "formlar:formEditor.sayfaBasligiYeni" : "formlar:formEditor.sayfaBasligiDuzenle");
    async function run() { await load(); }
    run();
  }, [load, isNew]);

  // ── Yazma yardımcısı: önce ekranda uygula, sonra uca yaz; hata olursa sunucudaki hâli geri oku ──
  async function mutate(call, apply) {
    setSaveState("saving");
    setErrorMsg("");
    apply?.();
    try {
      const res = await call();
      setSaveState("saved");
      return res ?? true;
    } catch (err) {
      console.error("[FormEditorPage]", err);
      setSaveState("error");
      setErrorMsg(err?.response?.data?.msg || tx("formEditor.kaydedilemedi"));
      await load();
      return null;
    }
  }

  const typeBody = (n) => ({
    name: n.name,
    mainQuestionQuestType: n.mainQuestType,
    ...(n.shortName ? { shortName: n.shortName } : {}),
    ...(n.mainQuestion ? { mainQuestion: n.mainQuestion } : {}),
  });
  function saveType(patch) {
    const next = { ...type, ...patch };
    mutate(() => updateItem("types", id, typeBody(next)), () => setType(next));
  }

  function renameCategory(catId, name) {
    mutate(
      () => updateItem("categories", catId, { name }),
      () => setCats((p) => p.map((c) => (c.categoryId === catId ? { ...c, categoryName: name } : c))),
    );
  }

  const questionBody = (q) => ({
    name: q.questionName,
    questType: q.questType,
    isPhotoRequiredPositive: q.photoRequiredPositive ? 1 : 0,
    isPhotoRequiredNeutral:  q.photoRequiredNeutral  ? 1 : 0,
  });
  function saveQuestion(catId, q, patch) {
    const next = { ...q, ...patch };
    mutate(
      () => updateItem("questions", q.questionId, questionBody(next)),
      () => setCats((p) => p.map((c) => (c.categoryId !== catId ? c : {
        ...c, questions: c.questions.map((x) => (x.questionId === q.questionId ? next : x)),
      }))),
    );
  }

  async function addCategory(name) {
    const res = await mutate(() => createItem("categories", { parentId: Number(id), name }));
    if (!res) return;
    setAdding(null);
    if (res.data != null) setCats((p) => [...p, { categoryId: res.data, categoryName: name, questions: [] }]);
    else await load();
  }

  async function addQuestion(cat, name, questType) {
    const res = await mutate(() => createItem("questions", {
      parentId: cat.categoryId, name, questType, isPhotoRequiredPositive: 0, isPhotoRequiredNeutral: 0,
    }));
    if (!res) return;
    setAdding(null);
    if (res.data != null) {
      const q = { questionId: res.data, questionName: name, questType, photoRequiredPositive: false, photoRequiredNeutral: false };
      setCats((p) => p.map((c) => (c.categoryId === cat.categoryId ? { ...c, questions: [...c.questions, q] } : c)));
    } else {
      await load();
    }
  }

  async function confirmDelete() {
    const d = deleteTarget;
    setDeleteTarget(null);
    if (!d) return;
    if (d.kind === "cat") {
      await mutate(
        () => softDeleteItem("categories", d.id),
        () => setCats((p) => p.filter((c) => c.categoryId !== d.id)),
      );
    } else {
      await mutate(
        () => softDeleteItem("questions", d.id),
        () => setCats((p) => p.map((c) => ({ ...c, questions: c.questions.filter((q) => q.questionId !== d.id) }))),
      );
      if (ncQuestion?.id === d.id) setNcQuestion(null);
    }
  }

  async function createType() {
    if (!draft.name.trim()) { setDraftErr(tx("formEditor.adZorunlu")); return; }
    if (!draft.scopeId)     { setDraftErr(tx("formEditor.kapsamZorunlu")); return; }
    setCreating(true);
    setDraftErr("");
    try {
      const res = await createItem("types", {
        parentId: Number(draft.scopeId),
        name: draft.name.trim(),
        mainQuestionQuestType: draft.mainQuestType,
        ...(draft.shortName.trim() ? { shortName: draft.shortName.trim().toUpperCase() } : {}),
        ...(draft.mainQuestion.trim() ? { mainQuestion: draft.mainQuestion.trim() } : {}),
      });
      navigate(`/Management/Forms/${res.data}/edit`, { replace: true });
    } catch (err) {
      console.error("[FormEditorPage] createType:", err);
      setDraftErr(err?.response?.data?.msg || tx("formEditor.kaydedilemedi"));
      setCreating(false);
    }
  }

  const answerTypeOptions = Object.keys(ANSWER_TYPE_KEYS).map((v) => (
    <option key={v} value={v}>{tx(`inspectionHierarchy.${ANSWER_TYPE_KEYS[v]}`)}</option>
  ));

  // ── Render ──────────────────────────────────────────────────────────────────
  const breadcrumb = (
    <BreadCrumb title={isNew ? tx("formEditor.yeniForm") : tx("formEditor.formuDuzenle")} pageTitle={tx("formEditor.formYonetimi")} />
  );

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          {breadcrumb}
          <div className="text-center py-5"><Spinner size="sm" className="me-2" />{tx("common:yukleniyor")}</div>
        </Container>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-content">
        <Container fluid>
          {breadcrumb}
          <Alert color="danger" className="d-flex align-items-center gap-2 py-2 small">
            <span>{tx("formEditor.formYuklenemedi")}</span>
            <Button size="sm" color="danger" outline className="ms-auto" onClick={() => { setLoading(true); load(); }}>
              {tx("formEditor.tekrarDene")}
            </Button>
            <Button size="sm" color="light" onClick={() => navigate("/Management/Forms/list")}>{tx("common:geri")}</Button>
          </Alert>
        </Container>
      </div>
    );
  }

  // Yeni form: ad + kapsam + ana soru → tip oluşturulur, düzenleyici açılır
  if (isNew) {
    return (
      <div className="page-content">
        <Container fluid>
          {breadcrumb}
          <Card className="border-0 shadow-sm">
            <CardBody>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <label className="form-label small fw-semibold mb-1">{tx("inspectionHierarchy.formAdi")}</label>
                  <Input bsSize="sm" value={draft.name} autoFocus
                    onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} />
                </Col>
                <Col xs={6} md={2}>
                  <label className="form-label small fw-semibold mb-1">{tx("formEditor.kisaKod")}</label>
                  <Input bsSize="sm" maxLength={15} value={draft.shortName}
                    onChange={(e) => setDraft((p) => ({ ...p, shortName: e.target.value.toUpperCase() }))} />
                </Col>
                <Col xs={12} md={4}>
                  <label className="form-label small fw-semibold mb-1">{tx("formEditor.kapsam")}</label>
                  <Input type="select" bsSize="sm" value={draft.scopeId}
                    onChange={(e) => setDraft((p) => ({ ...p, scopeId: e.target.value }))}>
                    <option value="">{tx("formEditor.kapsamSecin")}</option>
                    {scopes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </Input>
                </Col>
                <Col xs={12} md={8}>
                  <label className="form-label small fw-semibold mb-1">{tx("inspectionHierarchy.anaSoru")}</label>
                  <Input bsSize="sm" value={draft.mainQuestion}
                    onChange={(e) => setDraft((p) => ({ ...p, mainQuestion: e.target.value }))} />
                </Col>
                <Col xs={12} md={4}>
                  <label className="form-label small fw-semibold mb-1">{tx("inspectionHierarchy.cevapTipi")}</label>
                  <Input type="select" bsSize="sm" value={draft.mainQuestType}
                    onChange={(e) => setDraft((p) => ({ ...p, mainQuestType: Number(e.target.value) }))}>
                    {answerTypeOptions}
                  </Input>
                </Col>
              </Row>
              {draftErr && <Alert color="danger" className="py-2 small mt-3 mb-0">{draftErr}</Alert>}
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button color="light" size="sm" onClick={() => navigate("/Management/Forms/list")}>{tx("common:iptal")}</Button>
                <Button color="primary" size="sm" disabled={creating} onClick={createType}>
                  {creating ? <Spinner size="sm" /> : tx("formEditor.olustur")}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  const scopeLabel = scopes.find((s) => Number(s.id) === Number(type.scopeId))?.label;

  return (
    <div className="page-content">
      <Container fluid>
        {breadcrumb}
        <Row className="g-3">
          <Col xs={12} xl={ncQuestion ? 8 : 12}>
            {/* Form başlığı */}
            <Card className="border-0 bg-light mb-3">
              <CardBody className="py-3">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="flex-grow-1" style={{ minWidth: 220 }}>
                    <InlineText key={`n:${type.name}`} className="fw-semibold" value={type.name}
                      placeholder={tx("inspectionHierarchy.formAdi")} onSave={(v) => saveType({ name: v })} />
                  </div>
                  <div style={{ width: 130 }}>
                    <InlineText key={`s:${type.shortName}`} value={type.shortName} required={false} upper maxLength={15}
                      placeholder={tx("formEditor.kisaKod")} onSave={(v) => saveType({ shortName: v })} />
                  </div>
                  {scopeLabel && <Badge color="light" className="text-dark border">{scopeLabel}</Badge>}
                  <div className="ms-auto d-flex align-items-center gap-2">
                    {saveState === "saving" && (
                      <span className="text-muted small d-flex align-items-center gap-1">
                        <Spinner size="sm" />{tx("formEditor.kaydediliyor")}
                      </span>
                    )}
                    {saveState === "saved" && (
                      <span className="text-success small d-flex align-items-center gap-1">
                        <Check size={14} />{tx("formEditor.kaydedildi")}
                      </span>
                    )}
                    <Button color="light" size="sm" className="d-flex align-items-center gap-1"
                      onClick={() => navigate(`/Management/Forms/${id}/preview`)}>
                      <Eye size={14} />{tx("formEditor.onizle")}
                    </Button>
                    <Button color="light" size="sm" className="d-flex align-items-center gap-1"
                      onClick={() => navigate("/Management/Forms/list")}>
                      <ArrowLeft size={14} />{tx("common:geri")}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>

            {saveState === "error" && errorMsg && (
              <Alert color="danger" className="py-2 small">{errorMsg}</Alert>
            )}

            <FormFillView
              mainQuestType={type.mainQuestType}
              categories={cats}
              readOnly
              showProgress={false}
              renderMainQuestion={() => (
                <div className="d-flex gap-2 flex-wrap">
                  <div className="flex-grow-1" style={{ minWidth: 220 }}>
                    <InlineText key={`m:${type.mainQuestion}`} value={type.mainQuestion} required={false}
                      className="fw-semibold" placeholder={tx("formEditor.anaSoruYeri")}
                      onSave={(v) => saveType({ mainQuestion: v })} />
                  </div>
                  <Input type="select" bsSize="sm" style={{ width: "auto" }} value={type.mainQuestType}
                    onChange={(e) => saveType({ mainQuestType: Number(e.target.value) })}>
                    {answerTypeOptions}
                  </Input>
                </div>
              )}
              renderCategoryTitle={(cat) => (
                <InlineText key={`c:${cat.categoryId}:${cat.categoryName}`} className="fw-semibold" value={cat.categoryName}
                  placeholder={tx("formEditor.kategoriAdi")} onSave={(v) => renameCategory(cat.categoryId, v)} />
              )}
              renderCategoryTools={(cat) => (
                <button type="button" className="btn btn-sm btn-soft-danger p-1 d-inline-flex" title={tx("formEditor.kategoriyiSil")}
                  onClick={() => setDeleteTarget({ kind: "cat", id: cat.categoryId, name: cat.categoryName })}>
                  <Trash2 size={13} />
                </button>
              )}
              renderQuestionTitle={(q, cat) => (
                <InlineText key={`q:${q.questionId}:${q.questionName}`} value={q.questionName}
                  placeholder={tx("formEditor.soruMetni")} onSave={(v) => saveQuestion(cat.categoryId, q, { questionName: v })} />
              )}
              renderQuestionTools={(q) => (
                <button type="button" className="btn btn-sm btn-soft-danger p-1 d-inline-flex" title={tx("formEditor.soruyuSil")}
                  onClick={() => setDeleteTarget({ kind: "q", id: q.questionId, name: q.questionName })}>
                  <Trash2 size={13} />
                </button>
              )}
              renderQuestionExtras={(q, cat) => (
                <div className="ms-4 d-flex flex-wrap align-items-center gap-3 small">
                  <div className="d-flex align-items-center gap-1">
                    <span className="text-muted">{tx("inspectionHierarchy.cevapTipi")}</span>
                    <Input type="select" bsSize="sm" style={{ width: "auto" }} value={q.questType}
                      onChange={(e) => saveQuestion(cat.categoryId, q, { questType: Number(e.target.value) })}>
                      {answerTypeOptions}
                    </Input>
                  </div>
                  <label className="d-flex align-items-center gap-1 mb-0">
                    <input type="checkbox" className="form-check-input mt-0" checked={q.photoRequiredPositive}
                      onChange={(e) => saveQuestion(cat.categoryId, q, { photoRequiredPositive: e.target.checked })} />
                    {tx("inspectionHierarchy.fotografZorunluPozitifCevap")}
                  </label>
                  <label className="d-flex align-items-center gap-1 mb-0">
                    <input type="checkbox" className="form-check-input mt-0" checked={q.photoRequiredNeutral}
                      onChange={(e) => saveQuestion(cat.categoryId, q, { photoRequiredNeutral: e.target.checked })} />
                    {tx("inspectionHierarchy.fotografZorunluNotrNA")}
                  </label>
                  <Button size="sm" color={ncQuestion?.id === q.questionId ? "warning" : "soft-warning"}
                    onClick={() => setNcQuestion((p) => (p?.id === q.questionId ? null : { id: q.questionId, ad: q.questionName, _fromApi: true }))}>
                    {tx("formEditor.uygunsuzluklar")}
                  </Button>
                </div>
              )}
              renderCategoryFooter={(cat) => (
                adding?.kind === "q" && adding.catId === cat.categoryId ? (
                  <AddRow withType placeholder={tx("formEditor.soruMetni")}
                    onAdd={(text, questType) => addQuestion(cat, text, questType)} onCancel={() => setAdding(null)} />
                ) : (
                  <div className="p-2 border-top">
                    <Button size="sm" color="soft-primary" className="d-inline-flex align-items-center gap-1"
                      onClick={() => setAdding({ kind: "q", catId: cat.categoryId })}>
                      <Plus size={13} />{tx("formEditor.soruEkle")}
                    </Button>
                  </div>
                )
              )}
            />

            {adding?.kind === "cat" ? (
              <Card className="border-0 shadow-sm mb-3 overflow-hidden">
                <AddRow placeholder={tx("formEditor.kategoriAdi")} onAdd={addCategory} onCancel={() => setAdding(null)} />
              </Card>
            ) : (
              <Button color="primary" outline size="sm" className="d-inline-flex align-items-center gap-1 mb-4"
                onClick={() => setAdding({ kind: "cat" })}>
                <Plus size={14} />{tx("formEditor.kategoriEkle")}
              </Button>
            )}
          </Col>

          {ncQuestion && (
            <Col xs={12} xl={4}>
              <div style={{ position: "sticky", top: 70 }}>
                <NcTreePanel key={ncQuestion.id} question={ncQuestion} isFormSaved onClose={() => setNcQuestion(null)} />
              </div>
            </Col>
          )}
        </Row>
      </Container>

      {/* ── Silme onay modal'ı ── */}
      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered size="sm">
        <ModalHeader toggle={() => setDeleteTarget(null)}>
          {deleteTarget?.kind === "cat" ? tx("formEditor.kategoriyiSil") : tx("formEditor.soruyuSil")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {deleteTarget?.kind === "cat"
              ? tx("formEditor.kategoriSilOnay", { ad: deleteTarget?.name })
              : tx("formEditor.soruSilOnay", { ad: deleteTarget?.name })}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setDeleteTarget(null)}>{tx("common:iptal")}</Button>
          <Button color="danger" size="sm" onClick={confirmDelete}>{tx("common:sil")}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

// /new ve /:id/edit aynı bileşeni gösterir; id değişince (ör. oluşturma sonrası) sıfırdan kurulur.
function FormEditorPage() {
  const { id } = useParams();
  return <FormEditor key={id || "new"} id={id} />;
}

export default FormEditorPage;
